import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth.js'
import { updateProfile } from '../lib/api.js'
import { cn } from '../lib/cn.js'

// ── Helpers ───────────────────────────────────────────────────────────────────

export function onboardingKey(uid: string) {
  return `silkvoice-onboarded-${uid}`
}

export function isOnboarded(uid: string) {
  return localStorage.getItem(onboardingKey(uid)) === '1'
}

export function markOnboarded(uid: string) {
  localStorage.setItem(onboardingKey(uid), '1')
}

// ── Language data ─────────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: 'en', label: 'English',     nativeLabel: 'English',    flag: '🇺🇸' },
  { code: 'ja', label: 'Japanese',    nativeLabel: '日本語',      flag: '🇯🇵' },
  { code: 'zh', label: 'Chinese',     nativeLabel: '中文',        flag: '🇨🇳' },
  { code: 'de', label: 'German',      nativeLabel: 'Deutsch',     flag: '🇩🇪' },
  { code: 'hi', label: 'Hindi',       nativeLabel: 'हिंदी',       flag: '🇮🇳' },
  { code: 'fr', label: 'French',      nativeLabel: 'Français',    flag: '🇫🇷' },
  { code: 'ko', label: 'Korean',      nativeLabel: '한국어',       flag: '🇰🇷' },
  { code: 'pt', label: 'Portuguese',  nativeLabel: 'Português',   flag: '🇧🇷' },
  { code: 'it', label: 'Italian',     nativeLabel: 'Italiano',    flag: '🇮🇹' },
  { code: 'es', label: 'Spanish',     nativeLabel: 'Español',     flag: '🇪🇸' },
  { code: 'id', label: 'Indonesian',  nativeLabel: 'Bahasa',      flag: '🇮🇩' },
  { code: 'nl', label: 'Dutch',       nativeLabel: 'Nederlands',  flag: '🇳🇱' },
  { code: 'tr', label: 'Turkish',     nativeLabel: 'Türkçe',      flag: '🇹🇷' },
  { code: 'tl', label: 'Filipino',    nativeLabel: 'Filipino',    flag: '🇵🇭' },
  { code: 'pl', label: 'Polish',      nativeLabel: 'Polski',      flag: '🇵🇱' },
  { code: 'sv', label: 'Swedish',     nativeLabel: 'Svenska',     flag: '🇸🇪' },
  { code: 'bg', label: 'Bulgarian',   nativeLabel: 'Български',   flag: '🇧🇬' },
  { code: 'ro', label: 'Romanian',    nativeLabel: 'Română',      flag: '🇷🇴' },
  { code: 'ar', label: 'Arabic',      nativeLabel: 'العربية',     flag: '🇸🇦' },
  { code: 'cs', label: 'Czech',       nativeLabel: 'Čeština',     flag: '🇨🇿' },
  { code: 'el', label: 'Greek',       nativeLabel: 'Ελληνικά',    flag: '🇬🇷' },
  { code: 'fi', label: 'Finnish',     nativeLabel: 'Suomi',       flag: '🇫🇮' },
  { code: 'hr', label: 'Croatian',    nativeLabel: 'Hrvatski',    flag: '🇭🇷' },
  { code: 'ms', label: 'Malay',       nativeLabel: 'Melayu',      flag: '🇲🇾' },
  { code: 'sk', label: 'Slovak',      nativeLabel: 'Slovenčina',  flag: '🇸🇰' },
  { code: 'da', label: 'Danish',      nativeLabel: 'Dansk',       flag: '🇩🇰' },
  { code: 'ta', label: 'Tamil',       nativeLabel: 'தமிழ்',       flag: '🇱🇰' },
  { code: 'uk', label: 'Ukrainian',   nativeLabel: 'Українська',  flag: '🇺🇦' },
  { code: 'ru', label: 'Russian',     nativeLabel: 'Русский',     flag: '🇷🇺' },
  { code: 'hu', label: 'Hungarian',   nativeLabel: 'Magyar',      flag: '🇭🇺' },
  { code: 'no', label: 'Norwegian',   nativeLabel: 'Norsk',       flag: '🇳🇴' },
  { code: 'vi', label: 'Vietnamese',  nativeLabel: 'Tiếng Việt',  flag: '🇻🇳' },
  { code: 'mn', label: 'Mongolian',   nativeLabel: 'Монгол',      flag: '🇲🇳' },
] as const

type LangCode = (typeof LANGUAGES)[number]['code']

// ── Main component ────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const { t }    = useTranslation()
  const nav      = useNavigate()
  const { user } = useAuth()

  const [step,     setStep]     = useState(0)
  const [lang,     setLang]     = useState<LangCode | null>(null)
  const [animDir,  setAnimDir]  = useState<'forward' | 'back'>('forward')
  const [visible,  setVisible]  = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (user && isOnboarded(user.uid)) nav('/dashboard', { replace: true })
  }, [user, nav])

  const goNext = () => {
    setAnimDir('forward')
    setStep(s => s + 1)
  }

  const goBack = () => {
    setAnimDir('back')
    setStep(s => s - 1)
  }

  const handleFinish = async () => {
    if (user) {
      markOnboarded(user.uid)
      try {
        if (lang) await updateProfile({ preferredLang: lang })
      } catch { /* non-critical */ }
      nav('/dashboard', { replace: true })
    }
  }

  return (
    <div
      className="fixed inset-0 overflow-y-auto transition-opacity duration-400 z-10"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {/* Progress dots */}
      <div className="fixed top-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className={cn(
              'h-2 rounded transition-all duration-300 ease-spring',
              i === step ? 'w-6 bg-primary' : i < step ? 'w-2 bg-primary/40' : 'w-2 bg-glass-border',
            )}
          />
        ))}
      </div>

      {/* Step content — glass panel centered on the room */}
      <div className="relative z-[1] w-full max-w-[680px] mx-auto px-6 pt-20 pb-12 text-center min-h-full flex flex-col items-center justify-center">
        <div className="glass-panel rounded-xl shadow-glass p-10 w-full">
          <StepTransition step={step} animDir={animDir}>
            {step === 0 && <StepWelcome onNext={goNext} t={t} />}
            {step === 1 && <StepLanguage selectedLang={lang} onSelect={setLang} onNext={goNext} onBack={goBack} t={t} />}
            {step === 2 && <StepReady lang={lang} onFinish={handleFinish} onBack={goBack} t={t} />}
          </StepTransition>
        </div>
      </div>
    </div>
  )
}

// ── Step transition wrapper ───────────────────────────────────────────────────

function StepTransition({ step, animDir, children }: { step: number; animDir: 'forward' | 'back'; children: React.ReactNode }) {
  const [phase, setPhase]             = useState<'visible' | 'exiting' | 'entering'>('visible')
  const [cachedStep, setCachedStep]   = useState(step)
  const [cachedChildren, setCachedChildren] = useState(children)
  const prevStep = useRef(step)

  useEffect(() => {
    if (step === prevStep.current) return
    prevStep.current = step

    setPhase('exiting')
    const t1 = setTimeout(() => {
      setCachedStep(step)
      setCachedChildren(children)
      setPhase('entering')
    }, 200)
    const t2 = setTimeout(() => setPhase('visible'), 400)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [step, children])

  const content = phase === 'visible' ? children : cachedChildren

  const translateX = phase === 'exiting'
    ? (animDir === 'forward' ? '-40px' : '40px')
    : phase === 'entering'
    ? (animDir === 'forward' ? '40px' : '-40px')
    : '0px'

  return (
    <div
      key={cachedStep}
      style={{
        opacity:    phase === 'visible' ? 1 : 0,
        transform:  `translateX(${translateX})`,
        transition: phase === 'entering'
          ? 'opacity 250ms ease, transform 250ms cubic-bezier(0.34,1.56,0.64,1)'
          : 'opacity 180ms ease, transform 180ms ease',
      }}
    >
      {content}
    </div>
  )
}

// ── Step 0: Welcome ───────────────────────────────────────────────────────────

function StepWelcome({ onNext, t }: { onNext: () => void; t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div>
      {/* Gift badge */}
      <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded-full py-1.5 px-4 text-[0.82rem] font-semibold text-primary mb-5">
        🎁 {t('onboarding.welcome.giftBadge')}
      </div>

      <h1 className="text-[clamp(2rem,5vw,3rem)] font-extrabold leading-tight mb-4 tracking-tight bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">
        {t('onboarding.welcome.title')}
      </h1>
      <p className="text-lg text-text-muted max-w-[520px] mx-auto mb-10 leading-relaxed">
        {t('onboarding.welcome.subtitle')}
      </p>

      {/* Value props */}
      <div className="flex flex-col gap-3.5 max-w-[420px] mx-auto mb-12 text-left">
        {(['prop1', 'prop2', 'prop3'] as const).map((key) => (
          <div key={key} className="flex gap-3.5 items-start rounded-md p-3.5 px-4 bg-inset border border-inset-border">
            <span className="text-xl shrink-0 mt-px">
              {key === 'prop1' ? '🌍' : key === 'prop2' ? '🎭' : '🎁'}
            </span>
            <div>
              <div className="font-semibold text-[0.9rem] mb-0.5">
                {t(`onboarding.welcome.${key}.title`)}
              </div>
              <div className="text-[0.8rem] text-text-muted leading-snug">
                {t(`onboarding.welcome.${key}.desc`)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onNext}
        className="btn-cta"
      >
        {t('onboarding.welcome.cta')} →
      </button>

      <p className="text-[0.8rem] text-text-muted mt-4">
        {t('onboarding.welcome.ctaSub')}
      </p>
    </div>
  )
}

// ── Step 1: Language picker ───────────────────────────────────────────────────

function StepLanguage({
  selectedLang,
  onSelect,
  onNext,
  onBack,
  t,
}: {
  selectedLang: LangCode | null
  onSelect:    (lang: LangCode) => void
  onNext:      () => void
  onBack:      () => void
  t:           ReturnType<typeof useTranslation>['t']
}) {
  return (
    <div>
      <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-extrabold tracking-tight mb-3">
        {t('onboarding.language.title')}
      </h2>
      <p className="text-base text-text-muted mb-8">
        {t('onboarding.language.subtitle')}
      </p>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3 mb-10">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => onSelect(lang.code)}
            className={cn(
              'flex flex-col items-center gap-2 py-5 px-3 rounded-md cursor-pointer transition-all duration-200',
              selectedLang === lang.code
                ? 'border-2 border-primary bg-primary/[0.12] scale-[1.04] shadow-[0_4px_16px_rgba(232,98,26,0.25)]'
                : 'border border-inset-border bg-inset hover:bg-surface-raised',
            )}
          >
            <span className="text-3xl leading-none">{lang.flag}</span>
            <div>
              <div className="font-bold text-[0.85rem]">{lang.nativeLabel}</div>
              <div className="text-xs text-text-muted">{t('languages.' + lang.code)}</div>
            </div>
            {selectedLang === lang.code && (
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                ✓
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-3 justify-center">
        <button onClick={onBack} className="btn-ghost py-3 px-6 text-[0.95rem]">
          ← {t('common.back')}
        </button>
        <button
          onClick={onNext}
          disabled={!selectedLang}
          className={cn(
            'btn-cta py-3 px-8 text-[0.95rem] font-bold transition-all duration-200',
            !selectedLang && 'opacity-50 cursor-not-allowed',
          )}
        >
          {t('onboarding.language.cta')} →
        </button>
      </div>

      <p className="text-[0.8rem] text-text-muted mt-4">
        {t('onboarding.language.hint')}
      </p>
    </div>
  )
}

// ── Step 2: Ready ─────────────────────────────────────────────────────────────

function StepReady({
  lang,
  onFinish,
  onBack,
  t,
}: {
  lang:     LangCode | null
  onFinish: () => void
  onBack:   () => void
  t:        ReturnType<typeof useTranslation>['t']
}) {
  const selectedLang = LANGUAGES.find(l => l.code === lang)

  return (
    <div>
      <div className="text-5xl mb-3 leading-none animate-pop-in">
        🎉
      </div>
      <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-extrabold tracking-tight mb-3">
        {t('onboarding.ready.title')}
      </h2>
      <p className="text-base text-text-muted mb-2">
        {t('onboarding.ready.subtitle')}
        {selectedLang && (
          <span className="text-primary font-bold">
            {' '}{selectedLang.flag} {selectedLang.nativeLabel}
          </span>
        )}
      </p>
      <p className="text-sm text-text-muted mb-8">
        {t('onboarding.ready.desc')}
      </p>

      {/* Demo card */}
      <div className="bg-inset border border-inset-border rounded-md p-6 mb-8 text-left">
        <div className="text-[0.8rem] text-text-muted font-semibold uppercase tracking-widest mb-3">
          {t('onboarding.ready.exampleLabel')}
        </div>
        <div className="flex gap-3 items-center bg-inset rounded-sm p-3.5 px-4 mb-3">
          <span className="text-xl">📺</span>
          <div>
            <div className="font-semibold text-sm">{t('onboarding.ready.demoTitle')}</div>
            <div className="text-xs text-text-muted">{t('onboarding.ready.demoMeta')}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[0.85rem] text-text-muted">
          <span>{t('onboarding.ready.demoSource')} 🇺🇸</span>
          <span className="text-primary text-base font-bold">→</span>
          {selectedLang
            ? <span className="text-text font-semibold">{selectedLang.nativeLabel} {selectedLang.flag}</span>
            : <span>{t('onboarding.ready.yourLanguage')}</span>
          }
        </div>
      </div>

      <div className="flex gap-3 justify-center">
        <button onClick={onBack} className="btn-ghost py-3 px-6 text-[0.95rem]">
          ← {t('common.back')}
        </button>
        <button
          onClick={onFinish}
          className="btn-cta py-3 px-8 text-[0.95rem]"
        >
          {t('onboarding.ready.cta')} 🚀
        </button>
      </div>
    </div>
  )
}
